import classNames from "classnames";
import React, { useContext, useEffect, useRef, useState } from "react";
import { Message } from "semantic-ui-react";
import { useOnClickOutside } from "utilities/hookutils";
import { InPageEditingContext } from "./InPageEditingContext";
import { SaveOrCancelButtons } from "./SaveOrCancelButtons";
import { InPageEditableSectionField } from "./types";

export interface InPageEditableFieldProps {
  compact: boolean
  field: InPageEditableSectionField
  variant?: "default" | "sidePanel"
  darkerHover?: boolean
}

const variantClassNameDefinitions = {
  default: {
    notEditedWrapper: "main-section-template-fields-padding main-section-template-fields-editable-value",
    editedWrapper: "main-section-template-fields-padding main-section-template-fields-in-page-editing-value"
  },
  sidePanel: {
    notEditedWrapper: "main-section-template-fields-padding-side-panel main-section-template-fields-editable-value",
    editedWrapper: "main-section-template-fields-padding-side-panel main-section-template-fields-in-page-editing-value",
  },
}

export const InPageEditableField = ({
  compact,
  field,
  variant = "default",
  darkerHover,
}: InPageEditableFieldProps) => {
  const variantClassNames = variantClassNameDefinitions[variant];
  const [saveWithClickOutside, setSaveWithClickOutside] = useState<boolean>(false);
  const [isMouseDown, setIsMouseDown] = useState<boolean>(false);

  const {
    enabled,
    editingField,
    startInPageEdit,
    updateValue,
    saveInPageEdit,
    clearInPageEdit,
  } = useContext(InPageEditingContext);

  const inPageEditRef = useRef<HTMLDivElement>(null);
  const disableClickOutside = field.inPageEdit?.disableClickOutside ?? false;

  useOnClickOutside(inPageEditRef, (e) => onClickOutside(e));

  useEffect(() => {
    if (saveWithClickOutside) {
      setSaveWithClickOutside(false);
      saveInPageEdit(editingField.value);
    }
  }, [saveWithClickOutside]);

  const onClickOutside = (e: React.MouseEvent<HTMLElement>) => {
    const clickedInsidePopper = (e.target as HTMLElement)?.closest?.(".popup") ?? false;
    const clickedInsideModal = (e.target as HTMLElement)?.closest?.(".modals") ?? false; // ".modals" also covers the backdrop of the modal.
    if (disableClickOutside || clickedInsidePopper || clickedInsideModal) {
      return;
    }

    // Disable click outside if the data is currently being submitted. 
    if (editingField.isUpdating) {
      return;
    }

    // For the click outside, we need to schedule this event to occur after the any last events issued by the input components.
    //  This gives time for validation setting the final value, which is needed for a quite a few components.
    setTimeout(() => setSaveWithClickOutside(true), 0);
  }

  const onMouseUp = (e: React.MouseEvent<HTMLElement>) => {
    if (!isMouseDown) {
      return;
    }
    setIsMouseDown(false);

    const selectedText = window.getSelection()?.toString() || "";
    if (selectedText) {
      // If there is a selection, we don't want to start editing.
      return;
    }

    onClickInsideToStartEditing(e);
  }

  const onClickInsideToStartEditing = (e: React.MouseEvent<HTMLElement>) => {
    const clickedOnAnAnchor = (e.target as HTMLElement)?.closest?.("a") ?? false;
    const clickedOnButton = (e.target as HTMLElement)?.closest?.("button") ?? false;
    if (clickedOnAnAnchor || clickedOnButton) {
      return;
    }

    startInPageEdit(field);
  }

  const isInPageEditable = enabled && field.inPageEdit !== undefined && field.editDisabled !== true;
  const isEdited = editingField.fieldKey === field.key;

  if (!isInPageEditable) {
    return <div className={classNames(field.valueClassName, {
      "main-section-template-fields-padding": (!compact && !field.compact),
      "main-section-template-fields-empty": field.isEmpty,
      "main-section-template-fields-no-label": field.hideLabel,
      "main-section-template-fields-disabled": field.editDisabled,
    })}>
      {field.isEmpty ? <span>{RESX.Advancing.NoValue}</span> : field.render()}
    </div>
  }

  if (!isEdited) {
    return <div
      onMouseDown={() => setIsMouseDown(true)}
      onMouseUp={onMouseUp}
      className={classNames(field.valueClassName, variantClassNames.notEditedWrapper, {
        "main-section-template-fields-empty": field.isEmpty,
        "main-section-template-fields-no-label": field.hideLabel,
        "main-section-template-fields-error": field.error,
        "main-section-template-fields-cursor-loading": editingField.isUpdating,
        "in-page-editing-dark": darkerHover,
      })}>
      {field.isEmpty ? <span>{RESX.Advancing.NoValue}</span> : field.render()}
    </div>
  }

  if (editingField.isUpdating && editingField.fieldKey === field.key && !field.inPageEdit?.useCustomSavingState) {
    return <div className={classNames(field.valueClassName, variantClassNames.notEditedWrapper, "main-section-template-fields-in-page-editing-loading-wrapper", {
      "main-section-template-fields-empty": field.isEmpty,
      "main-section-template-fields-no-label": field.hideLabel,
      "main-section-template-fields-cursor-loading": editingField.isUpdating,
      "in-page-editing-dark": darkerHover,
    })}>
      {field.isEmpty ? <span>{RESX.Advancing.NoValue}</span> : field.render()}
      <div className={classNames("main-section-template-fields-in-page-editing-loading-animation", {
        "in-page-editing-dark": darkerHover,
      })} />
    </div>
  }

  const onChange = (value: any, submit: boolean) => {
    if (submit) {
      saveInPageEdit(value);
    } else {
      updateValue(value);
    }
  }

  return <div className={classNames(field.valueClassName, variantClassNames.editedWrapper, {
    "main-section-template-fields-no-label": field.hideLabel,
  })}>
    <div ref={inPageEditRef} className="main-section-template-fields-in-page-editing-form-wrapper">
      <div className={classNames("main-section-template-fields-in-page-editing-form", {
        "grow1": field.inPageEdit.fullWidth,
      })}>
        {field.inPageEdit.render({
          value: editingField.value,
          onChange: (value, submit) => onChange(value, submit),
          cancel: clearInPageEdit,
          submit: () => saveInPageEdit(editingField.value),
          error: Boolean(editingField.error),
          isSaving: editingField.isUpdating,
        })}
        {(field.inPageEdit.type === "confirmOrCancelButton" && !editingField.isUpdating) &&
          <SaveOrCancelButtons onSave={() => saveInPageEdit(editingField.value)} onCancel={clearInPageEdit} />}
      </div>
      {(field.inPageEdit.type === "confirmOrCancelButtonInline" && !editingField.isUpdating) &&
        <SaveOrCancelButtons onSave={() => saveInPageEdit(editingField.value)} onCancel={clearInPageEdit} inline />}
    </div>
    {editingField.error && <Message color="red">{editingField.error}</Message>}
  </div>
}